import React, { useMemo } from "react"
import DataTable from "react-data-table-component"
import { customStyles } from "../Common/DataTableStyles"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../../components/ui/dropdown-menu"
import { Button } from "../../../components/ui/button"
import { MoreVertical, Eye, Mail, Loader2 } from "lucide-react"

export const UserTableList = ({
    users,
    isLoading,
    getUserAvatar,
    getNameFromEmail,
    handleViewUser,
    handleConfirmationModal,
    resendInvitation,
    getUserTypeBadge,
    getStatusBadge,
    formatDate,
    isDropdownOpen,
    setIsDropdownOpen,
}) => {
    const columns = useMemo(() => [
        {
            name: "User",
            cell: (row) => (
                <div className="flex items-center space-x-3 py-2">
                    {getUserAvatar(row.userEmail)}
                    <div>
                        <div className="font-medium text-white">{getNameFromEmail(row.userEmail)}</div>
                        <div className="text-sm text-gray-400">{row.userEmail}</div>
                    </div>
                </div>
            ),
            grow: 2,
            sortable: true,
            selector: (row) => row.userEmail,
        },
        {
            name: "User Role",
            cell: (row) => getUserTypeBadge(row.roleId),
            sortable: true,
            selector: (row) => row.roleId,
        },
        {
            name: "Invited By",
            cell: (row) => (
                <div className="py-2">
                    <div className="font-medium text-white">{row.invitedFromUserName}</div>
                    <div className="text-sm text-gray-400">{row.invitedFromTenantName}</div>
                </div>
            ),
            sortable: true,
            selector: (row) => row.invitedFromUserName,
        },
        {
            name: "Invited On",
            selector: (row) => row.createdDate,
            sortable: true,
            cell: (row) => formatDate(row.createdDate),
        },
        {
            name: "Status",
            cell: (row) => getStatusBadge(row.notificationStatus),
            sortable: true,
            selector: (row) => row.notificationStatus,
        },
        {
            name: "Actions",
            cell: (row, index) => (
                <div className="flex justify-end w-full">
                    <DropdownMenu modal={false} onOpenChange={(isOpen) => setIsDropdownOpen(isOpen ? index : null)} open={isDropdownOpen === index}>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-white">
                                <MoreVertical className="h-4 w-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-black border-gray-800 text-white">
                            <DropdownMenuItem className="hover:bg-gray-800 focus:bg-gray-800" onClick={() => handleViewUser(row)}>
                                <Eye className="h-4 w-4 mr-2" /> View Details
                            </DropdownMenuItem>
                            {row?.notificationStatus !== "Accepted" && (
                                <DropdownMenuItem
                                    className="hover:bg-gray-800 focus:bg-gray-800"
                                    onClick={() => {
                                        setIsDropdownOpen(null);
                                        handleConfirmationModal(true, () => resendInvitation(row.id));
                                    }}>
                                    <Mail className="h-4 w-4 mr-2" /> Resend Invitation
                                </DropdownMenuItem>
                            )}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            right: true,
        },
    ], [isDropdownOpen]);

    return (
        <div className="bg-black rounded-lg shadow overflow-hidden border border-gray-800">
            <DataTable
                columns={columns}
                data={users}
                pagination
                progressPending={isLoading}
                progressComponent={
                    <div className="flex items-center justify-center p-10">
                        <Loader2 className="animate-spin h-8 w-8 text-white" />
                    </div>
                }
                noDataComponent={
                    <div className="text-center py-10 text-gray-400">
                        No users found
                    </div>
                }
                customStyles={customStyles}
                highlightOnHover
                pointerOnHover
                responsive
            />
        </div>
    )
}
